// The live solver session. It loads the scene's solver when the scene names one, and re-solves
// whenever the network changes: the clot moves or goes, the collaterals switch, or the scene time
// passes a thrombectomy. One solve is in flight at a time: the newest request waits its turn and
// replaces any older waiting one, and a result older than the newest request is dropped by its id,
// so a slow solve never queues behind a drag. Each result becomes the overlay the viewport colours
// arteries by. With the clot tool on, a click on an artery moves the clot there.

import { useAtlas, type AtlasState } from './store.ts';
import { liveActive, timelineOf } from './scene.ts';
import { clotsNow } from './stroke.ts';
import { scenarioById } from '../scenarios/index.ts';
import { defaultSource, type SimSource } from '../data/sims/files.ts';
import { collateralVessels, liveOverlay, LiveSolveError, loadHemoRt, structureVessels, type LiveRequest, type LiveResult, type LiveSolver } from '../data/sims/live.ts';
import type { Overlay } from '../data/sims/overlay.ts';
import { on } from '../viewport/events.ts';

const solvers = new Map<string, Promise<LiveSolver | null>>();
let current: LiveSolver | null = null;

/** The scene's solver once it has loaded (null while loading, or when the scene has none). */
export function liveSolver(): LiveSolver | null {
  return current;
}

/** Is this structure id in the loaded pack? */
export function packHas(s: Pick<AtlasState, 'catalogue'>): (id: string) => boolean {
  const byId = s.catalogue?.byId;
  return (id) => !!byId?.has(id);
}

/** The solver vessels a pack structure stands for, in network order. */
export function vesselsOf(structure: string, s: Pick<AtlasState, 'catalogue'> = useAtlas.getState()): string[] {
  return current ? (structureVessels(current.vessels, packHas(s)).get(structure) ?? []) : [];
}

/** A click on a structure with the clot tool on: the clot moves to its first vessel, keeping its
 *  position; a click on the clotted structure reopens it. */
export function clickStructure(structure: string): boolean {
  const ids = vesselsOf(structure);
  if (!ids.length) return false;
  const s = useAtlas.getState();
  const at = s.stroke.occlusion;
  s.setOcclusion(at && ids.includes(at.vessel) ? null : { vessel: ids[0]! });
  return true;
}

/** With the clot tool on, a click belongs to the tool: on an artery the solver models it places the clot, and the
 *  host selects that artery as usual. Anywhere else (a ghosted parcel, an artery the solver doesn't model, the
 *  background) it misses: the host neither selects nor deselects, and the panel says where to click. */
export function clotToolMisses(id: string | null, s: Pick<AtlasState, 'catalogue' | 'live' | 'stroke'> = useAtlas.getState()): boolean {
  return !!current && s.live.tool && liveActive(s) && !(id && vesselsOf(id, s).length);
}

/** Forget loaded solvers (tests). */
export function clearSolvers() {
  solvers.clear();
  current = null;
}

/** The picture beats with the solver's cycle only where the scene's clock is that cycle (Blood flow);
 *  elsewhere (Stroke's hours) it holds the cycle mean. */
export function liveBeats(s: Pick<AtlasState, 'scenario' | 'sims'>, solver: LiveSolver | null = current): boolean {
  const tl = timelineOf(s);
  const period = solver?.baseline.axis.period ?? 0;
  return !!tl?.periodic && period > 0 && Math.abs(tl.duration - period) <= 0.01 * period;
}

/** The network the scene asks the solver for now. */
export function liveRequest(s: Pick<AtlasState, 'scenario' | 'stroke' | 'time'>, solver: Pick<LiveSolver, 'vessels'>): LiveRequest {
  return { clots: clotsNow(s), absent: s.stroke.collaterals ? [] : collateralVessels(solver.vessels) };
}

const requestKey = (r: LiveRequest) => `${r.clots.map((c) => `${c.vessel}@${c.s}`).join(',')}|${(r.absent ?? []).join(',')}`;

/** The overlay a result colours the arteries with, by the slice's choice (none: the arteries keep their own colours). */
export function overlayFor(s: Pick<AtlasState, 'scenario' | 'sims' | 'stroke' | 'catalogue'>, solver: LiveSolver, r: LiveResult): Overlay | null {
  if (s.stroke.colourBy === 'none') return null;
  return liveOverlay(solver, r, packHas(s), { quantity: s.stroke.colourBy, mean: !liveBeats(s, solver) });
}

type Loader = (src: SimSource, path: string) => Promise<LiveSolver | null>;

function load(src: SimSource, path: string, loader: Loader): Promise<LiveSolver | null> {
  let p = solvers.get(path);
  if (!p) {
    p = loader(src, path);
    solvers.set(path, p);
    // A failed load isn't kept, so a solver that lands later loads on the next visit.
    p.catch(() => solvers.delete(path));
  }
  return p;
}

let stop: (() => void) | null = null;

/** Start the session (once per page; a second call replaces the first). Returns its stop function. */
export function startLive(src: SimSource = defaultSource(), loader: Loader = loadHemoRt): () => void {
  stop?.();
  const store = useAtlas;
  let gen = 0;
  // The newest request's id, the one solving, and the one waiting behind it.
  let latest = 0;
  let busy = false;
  let waiting: { id: number; req: LiveRequest } | null = null;

  const activate = async () => {
    const my = ++gen;
    current = null;
    latest++;
    waiting = null;
    const spec = scenarioById(store.getState().scenario).live;
    if (!spec) return;
    try {
      const solver = await load(src, spec.path, loader);
      if (my !== gen) return;
      current = solver;
      store.getState().setLive({ status: solver ? 'ready' : 'off' });
    } catch (err) {
      if (my !== gen) return;
      console.warn('[live] solver failed to load', err);
      store.getState().setLive({ status: 'error', error: err instanceof Error ? err.message : String(err) });
    }
  };

  const pump = async () => {
    if (busy || !waiting || !current) return;
    const { id, req } = waiting;
    waiting = null;
    busy = true;
    const solver = current;
    try {
      const result = req.clots.length || req.absent?.length ? await solver.solve(req) : solver.baseline;
      if (id === latest && solver === current) {
        const s = store.getState();
        s.setLive({ result, overlay: overlayFor(s, solver, result), error: undefined, note: undefined });
      }
    } catch (err) {
      if (id === latest && solver === current) {
        // The solver refusing a network (a cut leaving part of it with no inlet or outlet) is a note
        // beside the controls, never a toast; the last result stays.
        if (err instanceof LiveSolveError) store.getState().setLive({ note: err.message });
        else {
          console.warn('[live] solve failed', err);
          store.getState().setLive({ note: err instanceof Error ? err.message : String(err) });
        }
      }
    } finally {
      busy = false;
      void pump();
    }
  };

  const resolve = () => {
    const s = store.getState();
    latest++;
    waiting = null;
    if (!current || !liveActive(s)) {
      if (s.live.result || s.live.overlay || s.live.note) s.setLive({ result: null, overlay: null, note: undefined });
      return;
    }
    waiting = { id: latest, req: liveRequest(s, current) };
    void pump();
  };

  // Colour-by and the clock's kind change the overlay alone: no new solve.
  const recolour = () => {
    const s = store.getState();
    if (current && s.live.result) s.setLive({ overlay: overlayFor(s, current, s.live.result) });
  };

  const offs = [
    store.subscribe((s) => s.scenario, activate),
    store.subscribe(
      (s) => [s.live.status, liveActive(s), current ? requestKey(liveRequest(s, current)) : '', s.catalogue] as const,
      resolve,
      { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3] },
    ),
    store.subscribe(
      (s) => [s.stroke.colourBy, liveBeats(s)] as const,
      recolour,
      { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] },
    ),
    on('click', (e) => {
      const s = store.getState();
      // The host selects what was clicked as usual; the second click of a double-click doesn't move it back.
      if (!current || !s.live.tool || e.button !== 0 || e.count > 1 || !e.id) return;
      if (clickStructure(e.id) && s.live.missed) s.setLive({ missed: false });
    }),
  ];
  void activate();
  stop = () => {
    gen++;
    latest++;
    waiting = null;
    offs.forEach((off) => off());
    stop = null;
  };
  return stop;
}
